import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import useViewMessage from 'src/hooks/useViewMessage';
import useDeleteMessagesApi from 'src/services/messages_services/useDeleteMessagesApi';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import MessageActionPopUpContentWrapper from './MessageActionPopUpContentWrapper';
import useMessageButtonState from './useMessageButtonState';

interface DeleteMessageProps {
  clientId?: string;
  selectedUndel: Message[];
  selectedDel: Message[];
  origin?: string;
}

const DeleteMessage: FC<DeleteMessageProps> = ({
  clientId,
  selectedDel,
  selectedUndel,
  origin = 'outbox',
}) => {
  const { confirm } = useConfirm();
  const { refreshTable } = useViewMessage();
  const { t } = useTranslation();
  const { clientId: authClientId } = useAuth();

  //determine if deleting or undeleting and set values accordingly
  let messageIds: number[] = [];
  let isDelete = false;
  let messages: any[] = [];

  if (selectedDel.length > 0) {
    isDelete = true;
    messageIds = selectedDel.map((msg) => msg.messageId);
    messages = selectedDel;
  } else if (selectedUndel.length > 0) {
    isDelete = false;
    messageIds = selectedUndel.map((msg) => msg.messageId);
    messages = selectedUndel;
  }

  const { deleteMessage } = useDeleteMessagesApi(
    clientId ?? '',
    messageIds,
    isDelete,
    origin,
    messages
  );

  const [message, buttonState] = useMessageButtonState('Delete');
  const CountUndel = selectedUndel.length;
  const CountDel = selectedDel.length;

  const handleDeleteMessage = async () => {
    const isDeleteApproved = await confirm({
      title: t(`Delete Message(s)`),
      content: t(`Delete selected message(s)?`),
      confirmButtonText: t('OK'),
    });

    if (!isDeleteApproved) {
      return;
    }

    const messageIds = selectedDel.map((msg) => msg.messageId); //messageIds to DELETE

    //show error in browsers console if missing data
    if (!clientId || String(clientId).trim() === '') {
      console.error('clientId required to delete message(s)');
      toast.error(t('Unable to delete: No valid client ID'));
      return;
    }

    //show error in browsers console if missing data
    if (!messageIds || messageIds === undefined || messageIds.length === 0) {
      console.error('messageId(s) required to delete message(s)');
      toast.error(t('Unable to delete: No messages selected'));
      return;
    }

    // Enrich message objects with recipientClientId (use authClientId for signed-on user)
    const recipientClientId = authClientId || clientId;
    const enrichedMessages = selectedDel.map((msg) => ({
      ...msg,
      clientId: recipientClientId, // This will be used for RecipientClientId in the API
    }));

    try {
      const result = await deleteMessage({
        clientId,
        messageIds,
        isDeleteAction: true,
        messages: enrichedMessages,
        origin,
      }); // true means delete

      toast.success(t('Message(s) successfully deleted'));

      refreshTable(); // Refresh from server to get updated data
    } catch (error) {
      toast.error(t('Error deleting message(s)'));
      console.error(error);
    }
  };

  if (CountUndel === 0 && CountDel === 0) {
    return (
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[385px]" style={{ fontSize: '13.5px' }}>
            <MessageActionPopUpContentWrapper
              type="Information"
              contents={['Please select one or more records to Delete.']}
            />
          </div>
        }
        show={true}
      >
        <Button disabled={true} color="dark">
          Delete
        </Button>
      </PopUp>
    );
  }

  if (CountUndel > 0) {
    return (
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[385px]" style={{ fontSize: '13.5px' }}>
            <MessageActionPopUpContentWrapper
              type="Error"
              contents={selectedUndel.map(
                (msg) => `Message Id ${String(msg.messageId)} is not eligible to delete`
              )}
            />
          </div>
        }
        show={true}
      >
        <Button disabled={true} color="dark">
          Delete
        </Button>
      </PopUp>
    );
  } else {
    return (
      <Button disabled={CountDel === 0} color="error" onClick={handleDeleteMessage}>
        Delete
      </Button>
    );
  }
};

export default DeleteMessage;
